/**
 * useAutoToolMatch Hook
 *
 * Matches registered tools against the current prompt text by looking for
 * exact tool names mentioned in the prompt. Prompts often list the tools
 * they need explicitly (e.g., "- calculate_natal_chart_by_id"), so the
 * primary strategy is simple: check if the tool's name appears as-is.
 *
 * Matching priority:
 * 1. Exact tool name found in prompt (e.g., "calculate_natal_chart_by_id") → matched
 * 2. Display name (Chinese) found in prompt (e.g., "本命盘") → matched
 * 3. Everything else → not matched
 *
 * No fuzzy matching, no keyword extraction, no description scanning.
 */

import { useState, useCallback, useMemo } from 'react';
import { useAvailableTools, useAvailableMCPServers } from './useTools';
import type { MCPTool } from '../../../types/tools';

// ============================================================================
// Types
// ============================================================================

export interface ToolMatchResult {
  toolId: string;
  displayName: string;
  description: string;
  category: string;
  /** How the tool was matched */
  matchType: 'exact_name' | 'display_name' | 'sub_tool_name';
  /** The specific term(s) that matched in the prompt */
  matchedTerms: string[];
  type: 'custom' | 'builtin' | 'mcp';
  subTools?: string[];
  serverId?: string;
}

export interface AutoToolMatchState {
  isOpen: boolean;
  results: ToolMatchResult[];
  selectedTools: Set<string>;
  error: string | null;
}

// ============================================================================
// Matching Logic
// ============================================================================

/**
 * Check if a tool name (or any of its variants) appears in the prompt text.
 * Returns matched terms, or empty array if no match.
 */
function findToolInPrompt(
  promptText: string,
  toolName: string,
  displayName?: string | null,
): { matchType: 'exact_name' | 'display_name' | null; matchedTerms: string[] } {
  const matchedTerms: string[] = [];

  // 1. Exact tool name match (case-insensitive)
  //    e.g., "calculate_natal_chart_by_id" in prompt
  const nameLower = toolName.toLowerCase();
  // Strip common prefixes for matching (e.g., "builtin:" prefix)
  const cleanName = nameLower.replace(/^builtin:/, '');

  if (promptText.toLowerCase().includes(cleanName) && cleanName.length >= 4) {
    matchedTerms.push(toolName.replace(/^builtin:/, ''));
    return { matchType: 'exact_name', matchedTerms };
  }

  // 2. Display name match (Chinese names, must be 3+ chars to avoid false positives)
  if (displayName && displayName !== toolName) {
    const hasChineseChars = /[\u4e00-\u9fff]/.test(displayName);
    const minLength = hasChineseChars ? 3 : 4;
    if (displayName.length >= minLength && promptText.includes(displayName)) {
      matchedTerms.push(displayName);
      return { matchType: 'display_name', matchedTerms };
    }
  }

  return { matchType: null, matchedTerms: [] };
}

// ============================================================================
// Hook
// ============================================================================

interface UseAutoToolMatchOptions {
  currentCustomTools?: string | string[] | Record<string, any> | null;
  currentBuiltinTools?: string[] | { toolkits: string[]; enabled_tools?: string[] } | null;
  currentMcpServers?: string | string[] | Record<string, any> | null;
}

export function useAutoToolMatch(options: UseAutoToolMatchOptions = {}) {
  const { currentCustomTools, currentBuiltinTools, currentMcpServers } = options;

  const { data: toolsData, isLoading: toolsLoading } = useAvailableTools();
  const { data: mcpData, isLoading: mcpLoading } = useAvailableMCPServers();

  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState<ToolMatchResult[]>([]);
  const [selectedTools, setSelectedTools] = useState<Set<string>>(new Set());

  // Parse currently configured tools to exclude
  const configuredToolIds = useMemo(() => {
    const ids = new Set<string>();

    if (currentCustomTools) {
      try {
        let parsed = currentCustomTools;
        if (typeof parsed === 'string') parsed = JSON.parse(parsed);
        if (Array.isArray(parsed)) {
          parsed.forEach((t: string) => ids.add(t));
        } else if (parsed && typeof parsed === 'object') {
          const config = parsed as Record<string, any>;
          (config.enabled_tools || []).forEach((t: string) => ids.add(t));
        }
      } catch { /* ignore */ }
    }

    if (currentBuiltinTools) {
      try {
        let parsed = currentBuiltinTools;
        if (typeof parsed === 'string') parsed = JSON.parse(parsed as unknown as string);
        if (Array.isArray(parsed)) {
          parsed.forEach((t: string) => ids.add(t));
        } else if (parsed && typeof parsed === 'object' && 'toolkits' in parsed) {
          (parsed.toolkits || []).forEach((t: string) => ids.add(t));
        }
      } catch { /* ignore */ }
    }

    if (currentMcpServers) {
      try {
        let parsed = currentMcpServers;
        if (typeof parsed === 'string') parsed = JSON.parse(parsed);
        if (Array.isArray(parsed)) {
          parsed.forEach((t: string) => ids.add(t));
        } else if (parsed && typeof parsed === 'object') {
          const config = parsed as Record<string, any>;
          (config.servers || []).forEach((t: string) => ids.add(t));
          (config.enabled_tools || []).forEach((t: string) => ids.add(t));
        }
      } catch { /* ignore */ }
    }

    return ids;
  }, [currentCustomTools, currentBuiltinTools, currentMcpServers]);

  const runMatch = useCallback((promptText: string) => {
    if (!promptText?.trim() || !toolsData) {
      setResults([]);
      setSelectedTools(new Set());
      return;
    }

    try {
      const allResults: ToolMatchResult[] = [];

      // 1. Match custom tools — check each tool name against prompt
      for (const tool of (toolsData.custom_tools || [])) {
        if (configuredToolIds.has(tool.name)) continue;

        const { matchType, matchedTerms } = findToolInPrompt(
          promptText, tool.name, tool.display_name
        );

        if (matchType) {
          allResults.push({
            toolId: tool.name,
            displayName: tool.display_name || tool.name,
            description: tool.description,
            category: tool.category,
            matchType,
            matchedTerms,
            type: 'custom',
          });
        }
      }

      // 2. Match builtin toolkits — check toolkit name AND sub-tool names
      for (const toolkit of (toolsData.builtin_toolkits || [])) {
        if (configuredToolIds.has(toolkit.name)) continue;

        const subToolNames: string[] = [];
        const matchedSubTools: string[] = [];

        // Check sub-tool names first (more specific)
        if (toolkit.tools) {
          for (const subTool of toolkit.tools) {
            subToolNames.push(subTool.full_name);
            const { matchType: subMatch } = findToolInPrompt(
              promptText, subTool.name, subTool.display_name
            );
            if (subMatch) {
              matchedSubTools.push(subTool.display_name || subTool.name);
            }
          }
        }

        // Check toolkit-level name
        const { matchType, matchedTerms } = findToolInPrompt(
          promptText, toolkit.name, toolkit.display_name
        );

        if (matchType || matchedSubTools.length > 0) {
          allResults.push({
            toolId: toolkit.name,
            displayName: toolkit.display_name || toolkit.name,
            description: toolkit.description,
            category: toolkit.category,
            matchType: matchType || 'sub_tool_name',
            matchedTerms: [...matchedTerms, ...matchedSubTools],
            type: 'builtin',
            subTools: subToolNames,
          });
        }
      }

      // 3. Match MCP tools (group by server)
      if (mcpData?.mcp_tools) {
        const serverToolMap = new Map<string, MCPTool[]>();
        for (const tool of mcpData.mcp_tools) {
          const existing = serverToolMap.get(tool.server_id) || [];
          existing.push(tool);
          serverToolMap.set(tool.server_id, existing);
        }

        for (const [serverId, tools] of serverToolMap) {
          if (configuredToolIds.has(serverId)) continue;

          const matchedMcpTools: string[] = [];
          for (const tool of tools) {
            const { matchType: toolMatch } = findToolInPrompt(
              promptText, tool.name, tool.display_name
            );
            if (toolMatch) {
              matchedMcpTools.push(tool.display_name || tool.name);
            }
          }

          if (matchedMcpTools.length > 0) {
            allResults.push({
              toolId: serverId,
              displayName: serverId,
              description: tools.map(t => t.display_name || t.name).join(', '),
              category: 'MCP',
              matchType: 'exact_name',
              matchedTerms: matchedMcpTools,
              type: 'mcp',
              serverId,
              subTools: tools.map(t => t.namespaced_name),
            });
          }
        }
      }

      // Sort: exact_name first, then display_name, then sub_tool_name
      const matchOrder = { exact_name: 0, display_name: 1, sub_tool_name: 2 };
      allResults.sort((a, b) =>
        matchOrder[a.matchType] - matchOrder[b.matchType]
        || a.displayName.localeCompare(b.displayName)
      );

      setResults(allResults);
      // Pre-select all since these are exact matches (high confidence)
      setSelectedTools(new Set(allResults.map(r => r.toolId)));
    } catch (e) {
      console.warn('[useAutoToolMatch] matching failed:', e);
    }
  }, [toolsData, mcpData, configuredToolIds]);

  const openMatch = useCallback((promptText: string) => {
    setIsOpen(true);
    runMatch(promptText);
  }, [runMatch]);

  const closeMatch = useCallback(() => {
    setIsOpen(false);
    setResults([]);
    setSelectedTools(new Set());
  }, []);

  const toggleSelection = useCallback((toolId: string) => {
    setSelectedTools(prev => {
      const next = new Set(prev);
      if (next.has(toolId)) next.delete(toolId);
      else next.add(toolId);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedTools(new Set(results.map(r => r.toolId)));
  }, [results]);

  const deselectAll = useCallback(() => {
    setSelectedTools(new Set());
  }, []);

  const selectedResults = useMemo(() => {
    return results.filter(r => selectedTools.has(r.toolId));
  }, [results, selectedTools]);

  const getUpdatedConfigs = useCallback(() => {
    const selected = results.filter(r => selectedTools.has(r.toolId));

    const customToolNames: string[] = [];
    const builtinToolkitNames: string[] = [];
    const builtinSubToolNames: string[] = [];
    const mcpServerIds: string[] = [];
    const mcpToolNames: string[] = [];

    for (const result of selected) {
      if (result.type === 'custom') {
        customToolNames.push(result.toolId);
      } else if (result.type === 'builtin') {
        builtinToolkitNames.push(result.toolId);
        if (result.subTools) builtinSubToolNames.push(...result.subTools);
      } else if (result.type === 'mcp' && result.serverId) {
        mcpServerIds.push(result.serverId);
        if (result.subTools) mcpToolNames.push(...result.subTools);
      }
    }

    // Merge with existing configs
    let mergedCustomTools: string[] = [];
    if (currentCustomTools) {
      try {
        let parsed = currentCustomTools;
        if (typeof parsed === 'string') parsed = JSON.parse(parsed);
        if (Array.isArray(parsed)) mergedCustomTools = [...parsed];
        else if (parsed && typeof parsed === 'object')
          mergedCustomTools = [...((parsed as Record<string, any>).enabled_tools || [])];
      } catch { /* ignore */ }
    }
    mergedCustomTools = [...new Set([...mergedCustomTools, ...customToolNames])];

    let mergedBuiltinToolkits: string[] = [];
    let mergedBuiltinSubTools: string[] = [];
    if (currentBuiltinTools) {
      try {
        let parsed = currentBuiltinTools;
        if (typeof parsed === 'string') parsed = JSON.parse(parsed as unknown as string);
        if (Array.isArray(parsed)) mergedBuiltinToolkits = [...parsed];
        else if (parsed && typeof parsed === 'object' && 'toolkits' in parsed) {
          mergedBuiltinToolkits = [...(parsed.toolkits || [])];
          mergedBuiltinSubTools = [...(parsed.enabled_tools || [])];
        }
      } catch { /* ignore */ }
    }
    mergedBuiltinToolkits = [...new Set([...mergedBuiltinToolkits, ...builtinToolkitNames])];
    mergedBuiltinSubTools = [...new Set([...mergedBuiltinSubTools, ...builtinSubToolNames])];

    let mergedMcpServers: string[] = [];
    let mergedMcpTools: string[] = [];
    if (currentMcpServers) {
      try {
        let parsed = currentMcpServers;
        if (typeof parsed === 'string') parsed = JSON.parse(parsed);
        if (Array.isArray(parsed)) mergedMcpServers = [...parsed];
        else if (parsed && typeof parsed === 'object') {
          const config = parsed as Record<string, any>;
          mergedMcpServers = [...(config.servers || [])];
          mergedMcpTools = [...(config.enabled_tools || [])];
        }
      } catch { /* ignore */ }
    }
    mergedMcpServers = [...new Set([...mergedMcpServers, ...mcpServerIds])];
    mergedMcpTools = [...new Set([...mergedMcpTools, ...mcpToolNames])];

    const customConfig = mergedCustomTools.length > 0
      ? JSON.stringify(mergedCustomTools, null, 2) : null;

    let builtinConfig: string | null = null;
    if (mergedBuiltinToolkits.length > 0) {
      const config: { toolkits: string[]; enabled_tools?: string[] } = {
        toolkits: mergedBuiltinToolkits,
      };
      if (mergedBuiltinSubTools.length > 0) config.enabled_tools = mergedBuiltinSubTools;
      builtinConfig = JSON.stringify(config, null, 2);
    }

    let mcpConfig: string | null = null;
    if (mergedMcpServers.length > 0) {
      const config: { servers: string[]; enabled_tools?: string[] } = {
        servers: mergedMcpServers,
      };
      if (mergedMcpTools.length > 0) config.enabled_tools = mergedMcpTools;
      mcpConfig = JSON.stringify(config, null, 2);
    }

    return {
      customTools: customConfig,
      builtinTools: builtinConfig,
      mcpServers: mcpConfig,
      addedCount: {
        custom: customToolNames.length,
        builtin: builtinToolkitNames.length,
        mcp: mcpServerIds.length,
      },
    };
  }, [results, selectedTools, currentCustomTools, currentBuiltinTools, currentMcpServers]);

  return {
    isOpen,
    isLoading: toolsLoading || mcpLoading,
    results,
    selectedTools,
    selectedResults,
    openMatch,
    closeMatch,
    toggleSelection,
    selectAll,
    deselectAll,
    getUpdatedConfigs,
  };
}
